import { useCallback, useMemo, useState } from 'react';

import { componentMap, getComponentProps, handleLogicJump } from 'helpers/question-config';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import FormWrapper from '../Form/FormWrapper';

const RefereeFormWrapper = ({
  questions,
  logicJumps,
  textParams = {},
  brand,
  candidateId,
  getCandidateUpload,
  signCandidateUpload,
  createCandidateUpload,
  deleteCandidateUpload,
  onSubmit,
  locale = 'en_GB',
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [formCompleted, setFormCompleted] = useState(false);
  const [backward, setBackward] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());

  const handlers = {
    getCandidateUpload,
    signCandidateUpload,
    createCandidateUpload,
    deleteCandidateUpload,
  };

  const filteredQuestions = useMemo(() => {
    return questions.filter(q => !skippedQuestions.has(q.order));
  }, [questions, skippedQuestions]);

  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const keyboardHint = useKeyboardHints(currentQuestion?.type);

  const handleNext = useCallback(
    async formValues => {
      setBackward(false);

      if (currentQuestionIndex < filteredQuestions.length - 1) {
        const currentQuestionValue = formValues[currentQuestion.id.toString()];
        const { jumped, newSkipped, jumpToIndex } = handleLogicJump(
          formValues,
          logicJumps,
          currentQuestion,
          currentQuestionValue,
          skippedQuestions,
          filteredQuestions,
        );

        const updatedFiltered = questions.filter(q => !newSkipped.has(q.order));

        setSkippedQuestions(newSkipped);

        if (jumped) {
          const nextIndex = updatedFiltered.findIndex(q => q.order === jumpToIndex);
          setCurrentQuestionIndex(nextIndex !== -1 ? nextIndex : currentQuestionIndex + 1);
        } else {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
      } else {
        setFormCompleted(true);
        const filteredFormValues = Object.fromEntries(
          Object.entries(formValues).filter(([key]) =>
            filteredQuestions.some(q => q.id.toString() === key),
          ),
        );
        onSubmit(filteredFormValues);
      }
    },
    [
      currentQuestionIndex,
      filteredQuestions,
      currentQuestion,
      logicJumps,
      skippedQuestions,
      questions,
    ],
  );

  const handleBack = useCallback(() => {
    setBackward(true);
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
  }, [currentQuestionIndex]);

  const getQuestionProps = value =>
    getComponentProps(currentQuestion, value, candidateId, handlers, brand);

  return (
    <FormWrapper
      question={currentQuestion}
      questionIndex={currentQuestionIndex}
      totalQuestions={filteredQuestions.length}
      textParams={textParams}
      formCompleted={formCompleted}
      locale={locale}
      backward={backward}
      brand={brand}
      componentMap={componentMap}
      keyboardHint={keyboardHint}
      getComponentProps={getQuestionProps}
      onNext={handleNext}
      onBack={handleBack}
    />
  );
};

export default RefereeFormWrapper;
